import React, { useEffect, useState } from "react";
import { taskPaymentsApi, salaryPaymentsApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Users,
  Wallet,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PNLForecastPage = () => {
  const { formatAmount, convertAmount, displayCurrency, getCurrencySymbol } = useCurrency();
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startMonth, setStartMonth] = useState("2026-01");
  const [monthsCount, setMonthsCount] = useState(12);

  useEffect(() => {
    fetchData();
  }, [startMonth, monthsCount]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const forecastRes = await taskPaymentsApi.getPnlForecast(startMonth, monthsCount);
      setForecastData(forecastRes.data);
    } catch (error) {
      console.error("Error fetching PNL data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Format currency (value is already converted to display currency)
  const formatCurrency = (value) => {
    const symbol = getCurrencySymbol();
    return `${symbol}${(value || 0).toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  const formatMonthShort = (monthStr) => {
    // Parse month string "YYYY-MM" correctly to avoid timezone issues
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return monthNames[month - 1];
  };

  const formatMonthFull = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Navigate months
  const goToPreviousYear = () => {
    const [year, month] = startMonth.split("-").map(Number);
    setStartMonth(`${year - 1}-${String(month).padStart(2, "0")}`);
  };

  const goToNextYear = () => {
    const [year, month] = startMonth.split("-").map(Number);
    setStartMonth(`${year + 1}-${String(month).padStart(2, "0")}`);
  };

  // Prepare chart data with currency conversion
  const chartData = forecastData?.months?.map((m) => ({
    name: formatMonthShort(m.month),
    month: m.month,
    "Planned": convertAmount(m.total_planned, "UAH"),
    "Actual": convertAmount(m.total_actual, "UAH"),
    "Pending": convertAmount(m.total_pending, "UAH"),
    planned: convertAmount(m.total_planned, "UAH"),
    actual: convertAmount(m.total_actual, "UAH"),
    pending: convertAmount(m.total_pending, "UAH"),
  })) || [];

  // Current month
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentMonthIndex = chartData.findIndex((d) => d.month === currentMonth);

  // Calculate totals ONLY for current and past months (not future)
  const pastAndCurrentData = chartData.filter((d) => d.month <= currentMonth);
  const totalPlanned = pastAndCurrentData.reduce((sum, d) => sum + (d.planned || 0), 0);
  const totalActual = pastAndCurrentData.reduce((sum, d) => sum + (d.actual || 0), 0);
  const totalPending = pastAndCurrentData.reduce((sum, d) => sum + (d.pending || 0), 0);
  const variance = totalActual - totalPlanned;
  const variancePercent = totalPlanned > 0 ? ((variance / totalPlanned) * 100).toFixed(1) : 0;

  // Format for chart axis (short format with currency symbol)
  const formatAxisCurrency = (value) => {
    const symbol = getCurrencySymbol();
    if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${symbol}${(value / 1000).toFixed(0)}k`;
    return `${symbol}${value}`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
          <p className="text-zinc-50 font-medium mb-2">{formatMonthFull(data?.month)}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-zinc-400">Planned:</span>
              <span className="text-zinc-50 font-mono">{formatCurrency(data?.planned)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-green-400">Actual:</span>
              <span className="text-green-400 font-mono">{formatCurrency(data?.actual)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-orange-400">Pending:</span>
              <span className="text-orange-400 font-mono">{formatCurrency(data?.pending)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Generate year options
  const yearOptions = [];
  for (let y = 2024; y <= 2030; y++) {
    yearOptions.push(y);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="pnl-forecast-page">
      {/* Header with Period Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Plan vs Actual</h1>
          <p className="text-sm text-zinc-500">Expense forecast and variance analysis</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousYear} className="border-zinc-700 h-9 w-9">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Select value={startMonth} onValueChange={setStartMonth}>
            <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800 h-9" data-testid="start-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={`${year}-01`} className="text-zinc-50">
                  Jan {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(monthsCount)} onValueChange={(v) => setMonthsCount(Number(v))}>
            <SelectTrigger className="w-[100px] bg-zinc-900 border-zinc-800 h-9" data-testid="months-count-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="6" className="text-zinc-50">6 months</SelectItem>
              <SelectItem value="12" className="text-zinc-50">12 months</SelectItem>
              <SelectItem value="24" className="text-zinc-50">24 months</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={goToNextYear} className="border-zinc-700 h-9 w-9">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Plan/Month</span>
              <Calendar className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50">
              {formatCurrency(convertAmount(forecastData?.monthly_staff_cost || 0, "UAH"))}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Staff salaries</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <DollarSign className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatCurrency(totalActual)}</p>
            <p className="text-xs text-zinc-500 mt-1">YTD</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Wallet className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatCurrency(totalPending)}</p>
            <p className="text-xs text-zinc-500 mt-1">To pay</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Variance</span>
              {variance >= 0 ? (
                <TrendingUp className="h-4 w-4 text-red-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-green-500" />
              )}
            </div>
            <p className={cn("text-2xl font-bold", variance >= 0 ? "text-red-400" : "text-green-400")}>
              {variance >= 0 ? "+" : ""}{formatCurrency(variance)}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {variancePercent >= 0 ? "+" : ""}{variancePercent}% vs plan
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-orange-500" />
            Expense Chart ({formatMonthFull(startMonth)} - {monthsCount} months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#71717a", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#71717a", fontSize: 12 }}
                  tickFormatter={formatAxisCurrency}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                <Legend
                  wrapperStyle={{ paddingTop: "20px" }}
                  formatter={(value) => <span className="text-zinc-300 text-sm">{value}</span>}
                />
                {currentMonthIndex >= 0 && (
                  <ReferenceLine
                    x={chartData[currentMonthIndex]?.name}
                    stroke="#f97316"
                    strokeDasharray="5 5"
                    label={{
                      value: "Now",
                      position: "top",
                      fill: "#f97316",
                      fontSize: 11,
                    }}
                  />
                )}
                <Bar
                  dataKey="Planned"
                  fill="#3f3f46"
                  radius={[4, 4, 0, 0]}
                  name="Planned"
                />
                <Bar
                  dataKey="Actual"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                  name="Actual"
                />
                <Bar
                  dataKey="Pending"
                  fill="#f97316"
                  radius={[4, 4, 0, 0]}
                  name="Pending"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Users className="h-4 w-4 text-orange-500" />
            Monthly Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Plan (Staff)</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Actual (Staff)</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Actual (Artists)</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Total Actual</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Pending</TableHead>
                <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forecastData?.months?.map((row) => {
                const diff = row.total_actual - row.total_planned;
                const isCurrentMonth = row.month === currentMonth;
                const isFuture = row.month > currentMonth;

                return (
                  <TableRow
                    key={row.month}
                    className={cn(
                      "border-zinc-800 hover:bg-zinc-800/50",
                      isCurrentMonth && "bg-orange-500/5 border-l-2 border-l-orange-500"
                    )}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-50 font-medium">
                          {formatMonthFull(row.month)}
                        </span>
                        {isCurrentMonth && (
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-zinc-400">
                        {formatCurrency(row.planned_staff)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn("font-mono", row.actual_staff > 0 ? "text-green-400" : "text-zinc-600")}>
                        {formatCurrency(row.actual_staff)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn("font-mono", row.actual_artists > 0 ? "text-sky-400" : "text-zinc-600")}>
                        {formatCurrency(row.actual_artists)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn("font-mono font-medium", row.total_actual > 0 ? "text-zinc-50" : "text-zinc-600")}>
                        {formatCurrency(row.total_actual)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn("font-mono", row.total_pending > 0 ? "text-orange-400" : "text-zinc-600")}>
                        {formatCurrency(row.total_pending)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {isFuture ? (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">
                          Future
                        </Badge>
                      ) : row.total_actual === 0 && row.total_pending === 0 ? (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">
                          No Data
                        </Badge>
                      ) : diff > 0 ? (
                        <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20">
                          +{formatCurrency(diff)}
                        </Badge>
                      ) : diff < 0 ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                          {formatCurrency(diff)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                          On Plan
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PNLForecastPage;
